import { isWeeklyFrequency } from "./medicineDose";
import { medicineDoseConfig } from "./medicineDose";

export interface PresetMedicine {
  medicine_id: number;
  medicine_name: string;
  medicine_type: string;
  frequency: string;
  count_per_time: number | string;
  dosage: string;
  duration: string;
  quantity: number | string;
  meal_instruction: string;
  at_bedtime: boolean;
  instructions: string;
  medicine?: { id: number; name: string; brand?: string; type?: string; quantity?: number; mrpPrice?: number; batchNo?: string; expiryDate?: string; default_meal_instruction?: string };
}

export interface TreatmentPreset {
  diagnosis: string;
  on_examination: string;
  advice: string;
  follow_up_days: number | string | null;
  medicines: PresetMedicine[];
  warnings?: string[];
}

export const emptyTreatmentPreset = (): TreatmentPreset => ({ diagnosis: "", on_examination: "", advice: "", follow_up_days: "", medicines: [] });

export const presetFrequencies = [
  ["", "Select frequency"], ["OD-M", "Once daily - Morning"], ["OD-A", "Once daily - Afternoon"], ["OD-N", "Once daily - Night"],
  ["Twice a day (Every 12 hours) | BD / BID | 2", "Twice daily"],
  ["Three times a day (Every 8 hours) | TD / TID | 3", "Three times daily"],
  ["Four times a day (Every 6 hours) | QDS / QID | 4", "Four times daily"],
  ["Every 6 hours | q.6.h. | 4", "Every 6 hours"], ["As needed (Pro re nata) | PRN | 0", "As needed"],
  ["Once a week | QWK | 0.14", "Once a week"],
];

export const presetCalculatedQuantity = (item: PresetMedicine) => {
  const daily = isWeeklyFrequency(item.frequency) ? 1 / 7 : item.frequency.startsWith("OD-") ? 1 : Number(item.frequency.split("|").pop()?.trim());
  return medicineDoseConfig({ type: item.medicine_type }).autoQuantity && daily > 0 && Number(item.duration) > 0 && Number(item.count_per_time) > 0
    ? Math.ceil(daily * Number(item.duration) * Number(item.count_per_time)) : null;
};

export const updatePresetMedicine = (item: PresetMedicine, patch: Partial<PresetMedicine>): PresetMedicine => {
  const next = { ...item, ...patch };
  next.quantity = presetCalculatedQuantity(next) ?? next.quantity;
  return next;
};

export const presetPrescriptionItem = (item: PresetMedicine) => {
  const instruction_details = [];
  if (item.meal_instruction === "before_meal") instruction_details.push({ key: "instruction_before_meals", label: "Before meal" });
  if (item.meal_instruction === "after_meal") instruction_details.push({ key: "instruction_after_meals", label: "After meal" });
  if (item.at_bedtime) instruction_details.push({ key: "instruction_at_bedtime", label: "At bedtime" });
  return { medicine_id: item.medicine_id, medicine_name: item.medicine_name, medicine: item.medicine,
    frequency: item.frequency, count_per_time: Number(item.count_per_time), dosage: item.dosage,
    duration: item.duration, quantity: Number(item.quantity), instructions: item.instructions,
    instruction_before_meals: item.meal_instruction === "before_meal", instruction_after_meals: item.meal_instruction === "after_meal",
    instruction_at_bedtime: item.at_bedtime, instruction_details,
    unitPrice: item.medicine?.mrpPrice || 0, batchNo: item.medicine?.batchNo, expiryDate: item.medicine?.expiryDate };
};

export const followUpFromInterval = (days: number | string | null) => {
  if (!Number(days)) return "";
  const date = new Date();
  date.setDate(date.getDate() + Number(days));
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

export const mergePresetMedicines = <T extends { medicine_id?: number }>(existing: T[], preset: TreatmentPreset) => {
  const ids = new Set(existing.map((item) => Number(item.medicine_id)));
  return [...existing, ...preset.medicines.filter((item) => {
    if (ids.has(item.medicine_id)) return false;
    ids.add(item.medicine_id);
    return true;
  }).map(presetPrescriptionItem)];
};

export const applyPresetClinicalFields = <T extends { chief_complaint: string; diagnosis: string; on_examination: string; advice: string; follow_up_date: string }>(existing: T, complaint: string, preset: TreatmentPreset): T => {
  const complaints = existing.chief_complaint.split(/,|\n/).map((value) => value.trim()).filter(Boolean);
  if (!complaints.some((value) => value.toLowerCase() === complaint.toLowerCase())) complaints.push(complaint);
  return { ...existing, chief_complaint: complaints.join(", "),
    diagnosis: existing.diagnosis || preset.diagnosis, on_examination: existing.on_examination || preset.on_examination,
    advice: existing.advice || preset.advice, follow_up_date: existing.follow_up_date || followUpFromInterval(preset.follow_up_days) };
};
